import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { api, resolveImg } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [presale, setPresale] = useState([]);
  const [hero, setHero] = useState(null);

  useEffect(() => {
    api.get('/products', { params: { featured: true } }).then(({ data }) => {
      setFeatured(data);
      setHero(data[0] || null);
    });
    api.get('/products', { params: { presale: true } }).then(({ data }) => setPresale(data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden bg-surface-elevated/50">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <p className="eyebrow mb-6">Handcrafted in our atelier</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
              Sculptural<br />jewellery,<br /><span className="text-gold italic">gold plated</span>
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed mb-8 max-w-md">
              Ke-Sa creates bold, hand-shaped earrings, rings and cuffs — statement pieces in gold-plated
              brass, made to be worn and treasured.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="bg-gold text-primary-foreground px-8 py-4 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover transition-colors flex items-center gap-2">
                Shop the Collection <ArrowRight size={15} />
              </Link>
              <Link to="/presale" className="border border-border px-8 py-4 text-xs font-mono uppercase tracking-widest hover:border-gold hover:text-gold transition-colors">
                Presale Pieces
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
            className="relative aspect-[4/5] max-w-md mx-auto w-full">
            {hero && <img src={resolveImg(hero.images?.[0])} alt={hero.name} className="w-full h-full object-cover" />}
            <div className="absolute -bottom-5 -left-5 bg-background border border-border px-6 py-4">
              <p className="font-script text-2xl text-gold leading-none">{hero?.name}</p>
              <p className="text-xs text-muted-foreground mt-1">{hero?.description}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured */}
      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">Chosen by the maker</p>
            <h2 className="font-serif text-3xl lg:text-4xl">Signature Pieces</h2>
          </div>
          <Link to="/shop" className="text-sm text-gold hover:text-gold-hover flex items-center gap-1">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {featured.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Presale */}
      {presale.length > 0 && (
        <section className="border-y border-border bg-surface-elevated/50">
          <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 lg:py-28">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="eyebrow mb-3">Still on the bench — reserve yours</p>
                <h2 className="font-serif text-3xl lg:text-4xl">Atelier Presale</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {presale.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Service strip */}
      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
        {[
          { t: 'Made Entirely by Hand', d: 'Every piece is shaped, textured and polished in our studio.' },
          { t: 'Delivery, Your Schedule', d: 'Pick the exact day and courier window at checkout.' },
          { t: 'Secure Global Payment', d: 'Encrypted Stripe checkout, shipped worldwide.' },
        ].map((s) => (
          <div key={s.t}>
            <div className="gold-line w-16 mx-auto mb-6" />
            <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
